/**
 * Solar position in the browser, for the sun compass.
 *
 * The same maths the pipeline uses (`router/build/src/solar.rs`), duplicated
 * here because the dial has to respond instantly as the hour slider moves —
 * a round trip per pixel would be absurd for something this cheap.
 *
 * Accuracy is about half a degree, which is far finer than a 40-pixel dial can
 * show. The equation of time is ignored.
 */

export const CITY_LAT = 44.8154
export const CITY_LON = 20.4573
const STANDARD_MERIDIAN = 30.0

export interface SunPosition {
  /** Degrees above the horizon; negative is below. */
  altitude: number
  /** Degrees clockwise from north. */
  azimuth: number
}

const rad = (d: number) => (d * Math.PI) / 180
const deg = (r: number) => (r * 180) / Math.PI

function dayOfYear(d: Date): number {
  const start = Date.UTC(d.getFullYear(), 0, 0)
  return Math.floor((Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - start) / 86_400_000)
}

export function sunPosition(when: Date): SunPosition {
  const dec = rad(23.44 * Math.sin((2 * Math.PI / 365) * (dayOfYear(when) - 81)))
  const lat = rad(CITY_LAT)

  const localHour = when.getHours() + when.getMinutes() / 60
  const solarHour = localHour - (STANDARD_MERIDIAN - CITY_LON) / 15
  const hourAngle = rad(15 * (solarHour - 12))

  const sinAlt =
    Math.sin(lat) * Math.sin(dec) + Math.cos(lat) * Math.cos(dec) * Math.cos(hourAngle)
  const altitude = Math.asin(sinAlt)

  const cosAz =
    (Math.sin(dec) - Math.sin(altitude) * Math.sin(lat)) / (Math.cos(altitude) * Math.cos(lat))
  let azimuth = deg(Math.acos(Math.min(1, Math.max(-1, cosAz))))
  if (hourAngle > 0) azimuth = 360 - azimuth

  return { altitude: deg(altitude), azimuth }
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']

export function compassPoint(azimuth: number): string {
  return COMPASS[Math.round(((azimuth % 360) / 45)) % 8]
}

/**
 * Point the dial at the sun and the shadow away from it.
 *
 * Shadows fall opposite the sun, and lengthen as it drops — so the shadow line
 * grows toward the rim at low altitude, which is the thing worth seeing.
 */
export function renderSunDial(
  svg: SVGElement,
  text: HTMLElement,
  when: Date,
): SunPosition {
  const sun = sunPosition(when)
  const below = sun.altitude < 10

  const sunEl = svg.querySelector('.dial-sun') as SVGCircleElement
  const shadowEl = svg.querySelector('.dial-shadow') as SVGLineElement

  // Screen-up is north, matching the map's default orientation.
  const a = rad(sun.azimuth)
  // Nearer the rim when the sun is low.
  const r = 14 - 8 * Math.max(0, Math.min(1, sun.altitude / 90))
  sunEl.setAttribute('cx', String(20 + r * Math.sin(a)))
  sunEl.setAttribute('cy', String(20 - r * Math.cos(a)))

  // Shadow length grows as the sun drops: cot(altitude), clamped to the dial.
  const shadowLen = below
    ? 0
    : Math.min(17, 6 / Math.tan(rad(Math.max(6, sun.altitude))) + 4)
  shadowEl.setAttribute('x2', String(20 - shadowLen * Math.sin(a)))
  shadowEl.setAttribute('y2', String(20 + shadowLen * Math.cos(a)))

  svg.classList.toggle('is-night', below)

  text.textContent = below
    ? 'Sun below 10° — no useful shade'
    : `Sun ${Math.round(sun.altitude)}° up, from the ${compassPoint(sun.azimuth)} · ` +
      `shadows point ${compassPoint(sun.azimuth + 180)}`

  return sun
}
